import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { axiosInstance } from "@/shared/api/axiosConfig";
import { useAddAdditionalExperience } from "@/cards/common/api/taascard.api";
import { invalidateDeckMainCardByIdQueries } from "@/cards/common/utils/deckMainCardByIdQuery";

import {
  DEFAULT_IMPACT_ANCHOR_CUBES,
  createAnchorColumnId,
  createAnchorCubeId,
  cubeSectionKey,
  nextCubeTone,
  type AnchorCube,
  type AnchorCubeColumn,
} from "../utils/anchorCubes";
import type {
  ImpactColumnExperience,
  ImpactColumnExperienceType,
} from "../utils/columnExperience";
import { columnImageUrls, IMPACT_COLUMN_EXPERIENCE_META } from "../utils/columnExperience";
import {
  buildAddCubePayload,
  buildAddSubpagePayload,
  buildCubePatchBody,
  buildSubpagePatchBody,
  extractCreatedPageId,
  findImpactShellPageId,
  flattenImpactPages,
  mergeCubesWithApiPages,
  nextPageOrder,
  parentPageIdOf,
  resolveSubpageParentPageId,
  type ImpactApiPage,
} from "../utils/impactPages";

type AddCubeInput = {
  title: string;
  description?: string;
  afterCubeId?: string;
  experienceType?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
  sectionKey?: AnchorCube["sectionKey"];
  creator?: string;
};

type AddColumnInput = {
  title: string;
  description?: string;
  afterColumnId?: string;
  experienceType?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
};

type AnchorCubeContextValue = {
  cubes: AnchorCube[];
  saving: boolean;
  addCube: (input: AddCubeInput) => Promise<void>;
  editCube: (
    cubeId: string,
    input: {
      title: string;
      description?: string;
      experienceType?: ImpactColumnExperienceType;
      experience?: ImpactColumnExperience;
    }
  ) => Promise<void>;
  addColumn: (cubeId: string, input: AddColumnInput) => Promise<AnchorCubeColumn | undefined>;
  editColumn: (
    cubeId: string,
    columnId: string,
    input: {
      title: string;
      description?: string;
      experienceType?: ImpactColumnExperienceType;
      experience?: ImpactColumnExperience;
    }
  ) => Promise<void>;
  setColumnExperience: (
    cubeId: string,
    columnId: string,
    input: {
      experienceType: ImpactColumnExperienceType;
      experience?: ImpactColumnExperience;
    }
  ) => void;
  toggleCube: (cubeId: string) => void;
  removeCube: (cubeId: string) => Promise<void>;
  removeColumn: (cubeId: string, columnId: string) => Promise<void>;
};

function insertAfter<T extends { id: string }>(list: T[], afterId: string | undefined, item: T): T[] {
  if (!afterId) return [...list, item];
  const index = list.findIndex((entry) => entry.id === afterId);
  if (index < 0) return [...list, item];
  return [...list.slice(0, index + 1), item, ...list.slice(index + 1)];
}

const AnchorCubeContext = createContext<AnchorCubeContextValue | null>(null);

const storageKey = (cardId: number) => `hope-anchor-cubes:${cardId}`;

function readStoredCubes(cardId: number): AnchorCube[] | null {
  if (!cardId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey(cardId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AnchorCube[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredCubes(cardId: number, cubes: AnchorCube[]) {
  if (!cardId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(cardId), JSON.stringify(cubes));
  } catch {
    /* ignore quota */
  }
}

export function AnchorCubeProvider({
  cardId,
  pages: incomingPages = [],
  impactContentId,
  children,
}: {
  cardId: number;
  pages?: ImpactApiPage[];
  impactContentId?: number;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const { update: addPages } = useAddAdditionalExperience({
    key: ["addAdditionalExperience", cardId],
    params: {},
  });
  const pages = useMemo(() => flattenImpactPages(incomingPages), [incomingPages]);
  const [saving, setSaving] = useState(false);
  const [cubes, setCubes] = useState<AnchorCube[]>(
    () => mergeCubesWithApiPages(readStoredCubes(cardId), incomingPages)
  );

  const apiPageKey = pages
    .map(
      (page) =>
        `${page.id ?? ""}:${parentPageIdOf(page) ?? ""}:${page.order ?? ""}:${page.status ?? ""}:${String(page.content?.cube_id ?? "")}:${String(page.content?.column_id ?? "")}`
    )
    .join("|");

  useEffect(() => {
    setCubes(mergeCubesWithApiPages(readStoredCubes(cardId), pages));
  }, [apiPageKey, cardId, pages]);

  const persist = useCallback(
    (next: AnchorCube[]) => {
      setCubes(next);
      writeStoredCubes(cardId, next);
    },
    [cardId]
  );

  const refreshCard = useCallback(() => {
    invalidateDeckMainCardByIdQueries(queryClient, cardId);
    queryClient.invalidateQueries({ queryKey: ["main-card-by-id", cardId] });
  }, [cardId, queryClient]);

  const addCube = useCallback(
    async (input: AddCubeInput) => {
      const title = input.title.trim() || "Untitled";
      const cubeId = createAnchorCubeId();
      const thumbnailUrl = columnImageUrls(input.experience)[0];
      const nextCube: AnchorCube = {
        id: cubeId,
        title,
        description: input.description?.trim() || undefined,
        thumbnailUrl,
        experienceType: input.experienceType,
        experience: input.experience,
        icon: input.experienceType ? IMPACT_COLUMN_EXPERIENCE_META[input.experienceType].icon : "cube",
        tone: nextCubeTone(cubes.length),
        columns: [],
        collapsed: true,
        sectionKey: input.sectionKey,
        creator: input.creator,
      };

      if (!cardId) {
        persist(insertAfter(cubes, input.afterCubeId, nextCube));
        return;
      }

      setSaving(true);
      try {
        const response = await addPages(
          buildAddCubePayload({
            mainCardId: cardId,
            order: nextPageOrder(pages),
            title,
            description: nextCube.description,
            thumbnailUrl,
            cubeId,
            experienceType: input.experienceType,
            experience: input.experience,
            sectionKey: input.sectionKey,
          })
        );
        nextCube.pageId = extractCreatedPageId(response);
        persist(insertAfter(cubes, input.afterCubeId, nextCube));
        refreshCard();
        if (!nextCube.pageId) {
          enqueueSnackbar("Page created, but its id was not returned. Wait a moment before adding subpages.", {
            variant: "warning",
          });
        }
      } catch (error: any) {
        enqueueSnackbar(error?.response?.data?.message ?? error?.message ?? "Could not add this page.", {
          variant: "error",
        });
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [addPages, cardId, cubes, pages, persist, refreshCard]
  );

  const editCube = useCallback(
    async (
      cubeId: string,
      input: {
        title: string;
        description?: string;
        experienceType?: ImpactColumnExperienceType;
        experience?: ImpactColumnExperience;
      }
    ) => {
      const title = input.title.trim() || "Untitled";
      const description = input.description?.trim() || undefined;
      const cube = cubes.find((item) => item.id === cubeId);
      const experienceType = input.experienceType ?? cube?.experienceType;
      const experience = input.experience ?? cube?.experience;
      const thumbnailUrl = columnImageUrls(experience)[0] || cube?.thumbnailUrl;
      persist(
        cubes.map((item) =>
          item.id !== cubeId
            ? item
            : {
                ...item,
                title,
                description,
                thumbnailUrl,
                experienceType,
                experience,
                icon: experienceType ? IMPACT_COLUMN_EXPERIENCE_META[experienceType].icon : item.icon,
              }
        )
      );
      if (!cube?.pageId || cube.locked) return;
      setSaving(true);
      try {
        await axiosInstance.patch(
          `/main-card/pages/${cube.pageId}`,
          buildCubePatchBody({
            cubeId,
            title,
            description,
            thumbnailUrl,
            experienceType,
            experience,
          })
        );
        refreshCard();
      } catch (error: any) {
        enqueueSnackbar(error?.response?.data?.message ?? error?.message ?? "Could not save this page.", {
          variant: "error",
        });
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [cubes, persist, refreshCard]
  );

  const addColumn = useCallback(
    async (cubeId: string, input: AddColumnInput): Promise<AnchorCubeColumn | undefined> => {
      const cube = cubes.find((item) => item.id === cubeId);
      if (!cube) return undefined;
      const columnId = createAnchorColumnId();
      const nextColumn: AnchorCubeColumn = {
        id: columnId,
        title: input.title.trim() || "Untitled",
        description: input.description?.trim() || undefined,
        icon: input.experienceType
          ? IMPACT_COLUMN_EXPERIENCE_META[input.experienceType].icon
          : "doc",
        experienceType: input.experienceType,
        experience: input.experience,
      };

      const parentPageId = resolveSubpageParentPageId(cube, pages, impactContentId);
      if (cardId) {
        setSaving(true);
        try {
          const response = parentPageId
            ? await addPages(
                buildAddSubpagePayload({
                  mainCardId: cardId,
                  parentPageId,
                  order: nextPageOrder(pages),
                  cubeId,
                  columnId,
                  title: nextColumn.title,
                  description: nextColumn.description,
                  experienceType: input.experienceType,
                  experience: input.experience,
                  sectionKey: cubeSectionKey(cube),
                })
              )
            : await addPages(
                buildAddCubePayload({
                  mainCardId: cardId,
                  order: nextPageOrder(pages),
                  title: nextColumn.title,
                  description: nextColumn.description,
                  cubeId: columnId,
                  experienceType: input.experienceType,
                  experience: input.experience,
                  sectionKey: cubeSectionKey(cube),
                })
              );
          nextColumn.pageId = extractCreatedPageId(response);
          refreshCard();
        } catch (error: any) {
          enqueueSnackbar(error?.response?.data?.message ?? error?.message ?? "Could not add this subpage.", {
            variant: "error",
          });
          setSaving(false);
          throw error;
        } finally {
          setSaving(false);
        }
      }

      persist(
        cubes.map((item) =>
          item.id !== cubeId
            ? item
            : {
                ...item,
                collapsed: false,
                columns: input.afterColumnId
                  ? insertAfter(item.columns, input.afterColumnId, nextColumn)
                  : [nextColumn, ...item.columns],
              }
        )
      );
      return nextColumn;
    },
    [addPages, cardId, cubes, impactContentId, pages, persist, refreshCard]
  );

  const editColumn = useCallback(
    async (
      cubeId: string,
      columnId: string,
      input: {
        title: string;
        description?: string;
        experienceType?: ImpactColumnExperienceType;
        experience?: ImpactColumnExperience;
      }
    ) => {
      const cube = cubes.find((item) => item.id === cubeId);
      const column = cube?.columns.find((item) => item.id === columnId);
      if (!cube || !column) return;

      const title = input.title.trim() || "Untitled";
      const description = input.description?.trim() || undefined;
      const experienceType = input.experienceType ?? column.experienceType;
      const experience = input.experience ?? column.experience;
      let pageId = column.pageId;

      persist(
        cubes.map((item) =>
          item.id !== cubeId
            ? item
            : {
                ...item,
                columns: item.columns.map((entry) =>
                  entry.id !== columnId
                    ? entry
                    : {
                        ...entry,
                        title,
                        description,
                        experienceType,
                        experience,
                        icon: experienceType
                          ? IMPACT_COLUMN_EXPERIENCE_META[experienceType].icon
                          : entry.icon,
                      }
                ),
              }
        )
      );

      const parentPageId = resolveSubpageParentPageId(cube, pages, impactContentId);
      const patchBody = buildSubpagePatchBody({
        parentPageId,
        cubeId,
        columnId,
        title,
        description,
        experienceType,
        experience,
        sectionKey: column.sectionKey,
      });

      setSaving(true);
      try {
        if (pageId) {
          await axiosInstance.patch(`/main-card/pages/${pageId}`, patchBody);
        } else if (cardId && parentPageId) {
          const response = await addPages(
            buildAddSubpagePayload({
              mainCardId: cardId,
              parentPageId,
              order: nextPageOrder(pages),
              cubeId,
              columnId,
              title,
              description,
              experienceType,
              experience,
              sectionKey: column.sectionKey,
            })
          );
          pageId = extractCreatedPageId(response);
          if (pageId) {
            persist(
              (readStoredCubes(cardId) ?? cubes).map((item) =>
                item.id !== cubeId
                  ? item
                  : {
                      ...item,
                      columns: item.columns.map((entry) =>
                        entry.id === columnId ? { ...entry, pageId, title, description, experienceType, experience } : entry
                      ),
                    }
              )
            );
          }
        }
        refreshCard();
      } catch (error: any) {
        enqueueSnackbar(error?.response?.data?.message ?? error?.message ?? "Could not save this subpage.", {
          variant: "error",
        });
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [addPages, cardId, cubes, impactContentId, pages, persist, refreshCard]
  );

  const setColumnExperience = useCallback(
    (
      cubeId: string,
      columnId: string,
      input: {
        experienceType: ImpactColumnExperienceType;
        experience?: ImpactColumnExperience;
      }
    ) => {
      const column = cubes.find((cube) => cube.id === cubeId)?.columns.find((item) => item.id === columnId);
      void editColumn(cubeId, columnId, {
        title: column?.title ?? "Untitled",
        description: column?.description,
        experienceType: input.experienceType,
        experience: input.experience,
      });
    },
    [cubes, editColumn]
  );

  const toggleCube = useCallback(
    (cubeId: string) => {
      persist(
        cubes.map((cube) =>
          cube.id === cubeId ? { ...cube, collapsed: cube.collapsed === false } : cube
        )
      );
    },
    [cubes, persist]
  );

  const removeCube = useCallback(
    async (cubeId: string) => {
      const cube = cubes.find((item) => item.id === cubeId);
      persist(cubes.filter((item) => item.id !== cubeId));
      const shellId = findImpactShellPageId(pages, impactContentId);
      const remoteId = cube?.pageId;
      const canDeleteRemote = Boolean(remoteId) && !cube?.locked && remoteId !== shellId;
      if (canDeleteRemote && remoteId) {
        try {
          await axiosInstance.delete(`/main-card/pages/${remoteId}`);
          refreshCard();
        } catch {
          /* already removed locally */
        }
      }
    },
    [cubes, impactContentId, pages, persist, refreshCard]
  );

  const removeColumn = useCallback(
    async (cubeId: string, columnId: string) => {
      const column = cubes.find((item) => item.id === cubeId)?.columns.find((item) => item.id === columnId);
      persist(
        cubes.map((cube) =>
          cube.id !== cubeId
            ? cube
            : {
                ...cube,
                columns: cube.columns.filter((item) => item.id !== columnId),
              }
        )
      );
      if (column?.pageId) {
        try {
          await axiosInstance.delete(`/main-card/pages/${column.pageId}`);
          refreshCard();
        } catch {
          /* already removed locally */
        }
      }
    },
    [cubes, persist, refreshCard]
  );

  const value = useMemo(
    () => ({
      cubes,
      saving,
      addCube,
      editCube,
      addColumn,
      editColumn,
      setColumnExperience,
      toggleCube,
      removeCube,
      removeColumn,
    }),
    [cubes, saving, addCube, editCube, addColumn, editColumn, setColumnExperience, toggleCube, removeCube, removeColumn]
  );

  return createElement(AnchorCubeContext.Provider, { value }, children);
}

export function useAnchorCubes(): AnchorCubeContextValue {
  const value = useContext(AnchorCubeContext);
  if (!value) {
    return {
      cubes: DEFAULT_IMPACT_ANCHOR_CUBES,
      saving: false,
      addCube: async () => undefined,
      editCube: async () => undefined,
      addColumn: async () => undefined,
      editColumn: async () => undefined,
      setColumnExperience: () => undefined,
      toggleCube: () => undefined,
      removeCube: async () => undefined,
      removeColumn: async () => undefined,
    };
  }
  return value;
}
